import { Fragment, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Trophy } from 'lucide-react';
import {
  TOURNAMENT_RULE_SECTIONS,
  type TournamentRuleSection,
} from '../../rules/tournamentRules';
import { buildRulesIndexPath, isRulesLang, type RulesLang } from '../../seo/routes';
import './Rules.css';

interface RulesTournamentsPageProps {
  /** Torna all'INDICE delle regole: questa pagina non appartiene a una variante. */
  onBack: () => void;
  /**
   * Capitolo da mostrare all'arrivo, per chi non e' venuto a leggere il
   * regolamento ma a cercare UNA regola: gli aiuti dentro i tornei mandano qui.
   */
  initialAnchor?: string;
}

export default function RulesTournamentsPage({ onBack, initialAnchor }: RulesTournamentsPageProps) {
  const { t, i18n } = useTranslation();
  const lang: RulesLang = isRulesLang(i18n.language) ? i18n.language : 'en';

  /*
   * I corpi sono ARRAY di paragrafi, non una stringa sola con degli a-capo
   * dentro: e' cosi' che `white-space: pre-wrap` smette di essere l'unica cosa
   * che li tiene leggibili, e che la parita' i18n arriva a controllare anche
   * il NUMERO di paragrafi. Stesso gesto di `faq.entries` in FaqPage.
   */
  const righe = (chiave: string): string[] => {
    const grezzo = t(chiave, { returnObjects: true });
    return Array.isArray(grezzo) ? (grezzo as string[]) : [];
  };

  /*
   * Piu' semplice del gesto gemello nella FAQ (FaqPage), e non per caso: li' la
   * risposta stava dentro un <details> chiuso, e prima di scorrere bisognava
   * aprirlo. Qui i capitoli sono sempre aperti, quindi c'e' un passo solo.
   *
   * Corollario che la FAQ non ha mai potuto avere: siccome le sezioni portano
   * un `id` vero dentro HTML pre-renderizzato, arrivare a freddo su
   * `…/tornei#tornei-spareggi` scorre da solo, per conto del browser, anche con
   * JavaScript spento.
   */
  const sezioni = useRef(new Map<string, HTMLElement>());

  useEffect(() => {
    if (!initialAnchor) return;
    const el = sezioni.current.get(initialAnchor);
    if (!el) return;
    const motoRidotto = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    // `start` e non `center`: qui l'unita' e' un capitolo intero, e centrarlo
    // lascerebbe il titolo fuori schermo, sopra.
    el.scrollIntoView({ block: 'start', behavior: motoRidotto ? 'auto' : 'smooth' });
  }, [initialAnchor]);

  const renderSezione = (sezione: TournamentRuleSection) => {
    const scaglioni = sezione.rowsKey ? righe(sezione.rowsKey) : [];
    return (
      <section
        key={sezione.id}
        id={sezione.id}
        className="rl-card rl-cap-sec"
        ref={(el) => {
          if (el) sezioni.current.set(sezione.id, el);
          else sezioni.current.delete(sezione.id);
        }}
      >
        <h2 className="rl-cap-h">{t(sezione.titleKey)}</h2>
        {righe(sezione.bodyKey).map((paragrafo, i) => (
          <Fragment key={i}>
            <p className="rl-cap-p">{paragrafo}</p>
            {/*
              * Dopo il PRIMO paragrafo, non in fondo al capitolo: quel
              * paragrafo finisce con due punti ed e' la frase che presenta la
              * tabella («…per numero di iscritti:»). Nel testo di partenza —
              * una stringa sola — gli scaglioni venivano subito dopo, e
              * spostarli in coda lascerebbe quei due punti a introdurre il
              * nulla, con la tabella comparsa quattro paragrafi piu' giu'.
              */}
            {i === 0 && scaglioni.length > 0 && (
              <ul className="rl-trn-rounds">
                {scaglioni.map((riga) => (
                  <li key={riga}>{riga}</li>
                ))}
              </ul>
            )}
          </Fragment>
        ))}
      </section>
    );
  };

  return (
    <div className="rl-page">
      <div className="rl-inner">
        <div className="rl-crumb">
          {/* Un anello solo, non due: le sotto-pagine di presa e apertura
              stanno sotto una variante, questa sta sotto l'indice. */}
          <a
            href={buildRulesIndexPath(lang)}
            onClick={(e) => { e.preventDefault(); onBack(); }}
          >
            <span aria-hidden="true">←</span> {t('rules.eyebrow')}
          </a>
          <span className="rl-sep">/</span>
          <span className="rl-now">{t('rules.tournaments.title')}</span>
        </div>

        <div className="rl-card rl-hero rl-hero-3">
          <div className="rl-hero-deco">
            <div className="rl-hero-king" />
          </div>
          <div className="rl-hero-center">
            <span className="rl-eyebrow">{t('rules.eyebrow')}</span>
            <h1 className="rl-hero-title">{t('rules.tournaments.title')}</h1>
            <p className="rl-hero-sub">{t('rules.tournaments.heroSubtitle')}</p>
          </div>
          <div className="rl-hero-chip">
            <span className="rl-chip-ico">
              <Trophy />
            </span>
            <span className="rl-chip-txt">
              <span className="rl-chip-v">{TOURNAMENT_RULE_SECTIONS.length}</span>
              <span className="rl-chip-l">{t('rules.tournaments.chaptersLabel')}</span>
            </span>
          </div>
        </div>

        <div className="rl-card rl-cap-intro">
          <p className="rl-cap-p">{t('rules.tournaments.intro')}</p>
          <p className="rl-cap-src">{t('rules.tournaments.source')}</p>
        </div>

        {TOURNAMENT_RULE_SECTIONS.map(renderSezione)}
      </div>
    </div>
  );
}
